import { useQuery } from "@tanstack/react-query";
import client from "@phrmpc/infrastructure/phrmpc/axios";
import { resolveAttachmentContent } from "@phrmpc/shared/utils/resolveAttachmentContent";

type UseIncidentAttachmentContentParams = {
  incidentRecordId: string;
  fileId: string;
  fileName: string;
  enabled?: boolean;
};

/** Shared by useIncidentAttachmentContent and IncidentUploadCell's multi-file preview fetch (via
 *  useQueries), so both agree on the same query shape/cache key instead of diverging. */
export function incidentAttachmentContentQueryOptions({
  incidentRecordId,
  fileId,
  fileName,
  enabled = true,
}: UseIncidentAttachmentContentParams) {
  return {
    queryKey: ["incident-attachment-content", incidentRecordId, fileId],
    queryFn: async () => {
      const response = await client.get(
        `/incident-management/incident-records/${incidentRecordId}/files/${fileId}/content`,
        { responseType: "blob" },
      );
      const resolved = await resolveAttachmentContent(response, fileName);
      return URL.createObjectURL(resolved.blob);
    },
    enabled: enabled && Boolean(incidentRecordId) && Boolean(fileId),
    staleTime: Infinity,
  };
}

export function useIncidentAttachmentContent(
  params: UseIncidentAttachmentContentParams,
) {
  return useQuery(incidentAttachmentContentQueryOptions(params));
}

// Keyed by fileId - mirrors incidentAttachmentContentQueryOptions' own `staleTime: Infinity`/
// queryKey shape, so a preview opened twice for the same file doesn't refetch the blob or mint a
// second object URL.
const incidentAttachmentUrlCache = new Map<string, Promise<string>>();

/**
 * Plain-function counterpart to `useIncidentAttachmentContent`, for use as a `ControlledFileUploader`
 * `getFileUrl` callback - those are called lazily on preview/download click, not rendered, so they
 * can't call a React Query hook.
 */
export function resolveIncidentAttachmentUrl(
  incidentRecordId: string,
  fileId: string,
  fileName: string,
): Promise<string> {
  const cached = incidentAttachmentUrlCache.get(fileId);
  if (cached) return cached;

  const promise = client
    .get(`/incident-management/incident-records/${incidentRecordId}/files/${fileId}/content`, {
      responseType: "blob",
    })
    .then(async (response) => {
      const resolved = await resolveAttachmentContent(response, fileName);
      return URL.createObjectURL(resolved.blob);
    });

  incidentAttachmentUrlCache.set(fileId, promise);
  promise.catch(() => incidentAttachmentUrlCache.delete(fileId));
  return promise;
}
